import { useQuery } from '@tanstack/react-query';
import { garmentInspectionPath, pendingInspectionsPath } from '@core/api/queryPaths';

import { apiFetch } from './client';

// The types live in the shared core (docs/Mobile_Apps_Design.md); re-exported so imports stay put.
export type * from '@core/api/inspections';
import type {
  ApprovalQueue,
  DamageType,
  GarmentInspectionView,
  PendingInspection,
} from '@core/api/inspections';
/* ------------------------------------------------------- the damage master */

export const damageTypesKey = ['damage-charge', 'types'] as const;
export const damageChargesKey = ['damage-charge'] as const;

export function useDamageTypes(includeInactive = false) {
  return useQuery({
    queryKey: [...damageTypesKey, { includeInactive }],
    queryFn: () =>
      apiFetch<DamageType[]>(
        `/api/damage-charge/types${includeInactive ? '?includeInactive=true' : ''}`,
      ),
  });
}

/* ---------------------------------------------------------- the inspection */

export const inspectionsKey = ['return-inspection'] as const;
export const pendingInspectionsKey = [...inspectionsKey, 'pending'] as const;

export function usePendingInspections(includeInspected = false) {
  return useQuery({
    queryKey: [...pendingInspectionsKey, { includeInspected }],
    queryFn: () => apiFetch<PendingInspection[]>(pendingInspectionsPath(includeInspected)),
  });
}

export function useGarmentInspection(rentalOrderItemId: number | null) {
  return useQuery({
    queryKey: [...inspectionsKey, 'garment', rentalOrderItemId],
    queryFn: () => apiFetch<GarmentInspectionView>(garmentInspectionPath(rentalOrderItemId ?? 0)),
    enabled: rentalOrderItemId !== null,
  });
}

/* ------------------------------------------------------- the approval queue */

export const approvalQueueKey = [...inspectionsKey, 'awaiting-approval'] as const;

export function useApprovalQueue() {
  return useQuery({
    queryKey: approvalQueueKey,
    queryFn: () => apiFetch<ApprovalQueue>('/api/return-inspection/awaiting-approval'),
    // A manager signing one off at the same moment a clerk corrects another is the ordinary way
    // this list goes stale, and it is the list whose staleness holds up a settlement.
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}
